import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import axios from 'axios';

const baseUrl = import.meta.env.VITE_API_URL;
const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
	config: {
		instrumentationKey: '',
		extensions: [reactPlugin],
		extensionConfig: {
			[reactPlugin.identifier]: {},
		},
		enableCorsCorrelation: true,
		disableAjaxTracking: false,
		disableFetchTracking: false,
		enableAutoRouteTracking: false,
		enableUnhandledPromiseRejectionTracking: false,
		disablePageUnloadEvents: ['disabled'],


		distributedTracingMode: 1,
		correlationHeaderDomains: [
			window.location.host,
			baseUrl,
		],
	},
});

let isAppInsightsInitialized = false;

export function initializeAppInsights(instrumentationKey: string) {
	if (isAppInsightsInitialized) {
		return;
	}

	if (!instrumentationKey) {
		console.warn('App Insights instrumentation key is not provided. Skipping initialization.');
		return;
	}

	appInsights.config.instrumentationKey = instrumentationKey;
	appInsights.loadAppInsights();
	isAppInsightsInitialized = true;

	//Setup a stack trace in axios to get better error tracking
	axios.interceptors.response.use(
		response => response,
		error => {
			if (error.response) {
				const err = new Error(`HTTP ${error.response.status} ${error.response.statusText} ${error.config.method?.toUpperCase() || ''} ${error.config.url}`);
				err.stack = filterAxiosStack(error.stack);
				err.name = `${error.config.method?.toUpperCase() || ''} ${error.config.url}`;


				const sanitizedHeaders = { ...error.config.headers };
				if (sanitizedHeaders.Authorization) {
					sanitizedHeaders.Authorization = '[REDACTED]';
				}
				if (sanitizedHeaders.authorization) {
					sanitizedHeaders.authorization = '[REDACTED]';
				}

				appInsights.trackException({
					exception: err,
					properties: {
						method: error.config.method?.toUpperCase(),
						url:error.config.url,
						headers: JSON.stringify(sanitizedHeaders),
						appLocation:window.location.href,
					},
					measurements: {
						statusCode: error.response.status,
						responseTime: error.response.headers['x-response-time'] ? parseInt(error.response.headers['x-response-time'], 10) : 0,
					},
				});
			} else {
				const err = new Error('Network error or no response received');
				err.stack = error.stack || new Error(); // Preserve the original stack trace
				appInsights.trackException({
					exception: err,
					properties: {
						message: error.message,
					},
				});
			}
			return Promise.reject(error);
		}
	);
}

// This function filters out lines from the stack trace that contain 'axios' or 'node_modules/axios'.
// This helps to reduce noise in the error tracking and focuses on the relevant parts of the stack trace.
function filterAxiosStack(stack?: string): string | undefined {
	if (!stack) {return undefined;}
	// Remove lines containing 'axios' or 'node_modules/axios'
	return stack
		.split('\n')
		.filter(line => !line.includes('axios') && !line.includes('node_modules'))
		.join('\n');
}

// This telemetry initializer filters out telemetry related to Clarity
appInsights.addTelemetryInitializer((envelope) => {
	// For dependency/request telemetry, check the target or name
	if (
		envelope.data?.baseType === 'RemoteDependencyData' &&
    envelope.data.baseData?.target?.includes('clarity.microsoft.com')
	) {
		// Returning false drops the telemetry item
		return false;
	}
	// For AJAX calls, you might also want to check the URL
	if (
		envelope.data?.baseType === 'RemoteDependencyData' &&
    envelope.data.baseData?.data?.includes('clarity.microsoft.com')
	) {
		return false;
	}



	//If we need to filter out 401/403:
	// if (
	//   envelope.data?.baseType === "ExceptionData" &&
	//   (envelope.data.baseData?.measurements?.statusCode === 401 || envelope.data.baseData?.measurements?.statusCode === 403)
	// ) {
	//   return false;
	// }

	// For page views or other types, add more conditions as needed
	return true;
});

function setInsightsUser(user:CurrentUserDTO) {
	if (!isAppInsightsInitialized) {
		console.warn('Application Insights is not initialized. Cannot set user.');
		return;
	}
	appInsights.setAuthenticatedUserContext(user.id?.toString() || 'unknown', user.email, true);
}


export default { setInsightsUser,initializeAppInsights };